import { PatientActionHistoryActionInsiDetailsInput } from '@saas/interfaces/graphql'
import {
    INSIConnectorGetInsFromIdentityInformation,
    INSIConnectorGetInsFromVitaleCardResult,
    INSIConnectorResultData,
    INSIConnectorResultError,
    INSIConnectorVerifyPatientIdentityResult,
} from '@saas/interfaces/insi'
import { useCallback, useMemo } from 'react'
import {
    CreatePatientActionHistoryMutationVariables,
    useCreatePatientActionHistoryMutation,
} from '../../api/mutations/__generated__/createPatientActionHistory.gql'
import useCurrentUser from '../../hooks/useCurrentUser'
import { capitalizeFirstLetter } from '../../utils/stringUtils'
import { historyActionType } from '../Patient/Form/_utils'
import { TraceInsiCall, UseTraceInsiCallProps } from './_props'
import { useInsiStore } from './_store'
import { buildIdentityInformations } from './_utils'

const logData = (method: string, { data }: INSIConnectorResultData | INSIConnectorResultError) => {
    if (window._env_.REACT_APP_INSI_CONNECTOR_DEBUG === 'true') {
        console.log({
            method,
            data,
        })
    }
}

export const getInsiErrorMessage = (reason: string) => {
    const baseMessage = 'Erreur du téléservice INSi'
    return `${baseMessage} : ${capitalizeFirstLetter(reason)}`
}

const useTraceInsiCall = (props?: UseTraceInsiCallProps) => {
    const patientId = props?.patientId

    const insiStore = useInsiStore()

    const user = useCurrentUser()
    const [createPatientActionHistoryMutation, { loading, error }] = useCreatePatientActionHistoryMutation()

    const baseVariables = useMemo<
        Pick<CreatePatientActionHistoryMutationVariables, 'patientId' | 'actionType' | 'updatedById' | 'isTechnical'>
    >(
        () => ({
            patientId: patientId ?? '',
            actionType: historyActionType.SYNC,
            isTechnical: false,
            updatedById: user.id,
        }),
        [patientId, user.id],
    )

    const mutate = useCallback(
        async (actionInsiDetails: PatientActionHistoryActionInsiDetailsInput, forcePatientId?: string) => {
            const mutationPatientId = forcePatientId || baseVariables.patientId

            await createPatientActionHistoryMutation({
                variables: {
                    ...baseVariables,
                    patientId: mutationPatientId,
                    actionInsiDetails,
                },
            })
        },
        [baseVariables, createPatientActionHistoryMutation],
    )

    const verifyPatientInsIdentity: TraceInsiCall<INSIConnectorVerifyPatientIdentityResult> = async (response) => {
        logData('verifyPatientInsIdentity', response)
        const { data } = response

        if (data.s_status === 'OK') {
            await mutate({
                callType: 'VERIFICATION',
                returnType: 'type_00',
            })

            return
        }

        if (!data || data.s_status === 'ERROR') {
            if (data.i_apiErrorCode === 3) {
                await mutate({
                    callType: 'VERIFICATION',
                    returnType: 'type_01',
                })
                throw new Error(getInsiErrorMessage(data?.s_apiErrorExtendedInformations ?? 'inconnue'))
            } else {
                throw new Error(getInsiErrorMessage(data?.s_apiErrorTlsiErrorSoapReason ?? 'inconnue'))
            }
        }
    }

    const getPatientInsIdentityFromVitaleCard: TraceInsiCall<INSIConnectorGetInsFromVitaleCardResult> = async (
        response,
    ) => {
        const { data } = response
        logData('getPatientInsIdentityFromVitaleCard', response)

        if (data.s_status === 'OK') {
            if (data.i_insIdentityResult === 2) {
                await mutate({
                    callType: 'VITALE_CARD',
                    returnType: 'type_00',
                    insNum: data.Identity.Ins.s_value,
                    oid: data.Identity.Ins.s_oid,
                    insHistory: data.Identity?.History ? JSON.stringify(data.Identity?.History) : undefined,
                })
            }

            if (data.i_insIdentityResult === 3) {
                // multiple identities found
                await mutate({
                    callType: 'VITALE_CARD',
                    returnType: 'type_02',
                })
                throw new Error(getInsiErrorMessage(`plusieurs identités trouvées`))
            }

            if (data.i_insIdentityResult === 1) {
                // no identity found
                await mutate({
                    callType: 'VITALE_CARD',
                    returnType: 'type_01',
                })
                throw new Error(getInsiErrorMessage(`aucun patient trouvé`))
            }
        }

        if (data.s_status === 'ERROR') {
            if (data.i_apiErrorCode === 4096) {
                await mutate({
                    callType: 'VITALE_CARD',
                    returnType: 'type_01',
                })
                return
            }

            if (data.i_apiErrorCode === 3) {
                // multiple identities found
                await mutate({
                    callType: 'VITALE_CARD',
                    returnType: 'type_02',
                })
                throw new Error(getInsiErrorMessage(`plusieurs identités trouvées`))
            }

            if (data.i_apiErrorCode === 1) {
                // no identity found
                await mutate({
                    callType: 'VITALE_CARD',
                    returnType: 'type_01',
                })
                throw new Error(getInsiErrorMessage(`aucun patient trouvé`))
            }

            throw new Error(getInsiErrorMessage(data?.s_apiErrorTlsiErrorSoapReason ?? 'inconnue'))
        }
    }

    const getPatientInsIdentityFromIdentityInformation: TraceInsiCall<
        INSIConnectorGetInsFromIdentityInformation
    > = async (params) => {
        const { data, delayed } = params
        logData('getPatientInsIdentityFromIdentityInformation', params)

        if (data.s_status === 'OK') {
            let res: PatientActionHistoryActionInsiDetailsInput | undefined

            if (data.i_insIdentityResult === 3) {
                // multiple identities found
                res = {
                    callType: 'IDENTITY_TRAITS',
                    returnType: 'type_02',
                }
            }
            if (data.i_insIdentityResult === 2) {
                // single identity found
                res = {
                    callType: 'IDENTITY_TRAITS',
                    returnType: 'type_00',
                    insNum: data.Identity.Ins.s_value,
                    oid: data.Identity.Ins.s_oid,
                    insHistory: data.Identity?.History ? JSON.stringify(data.Identity?.History) : undefined,
                    identityTraits: buildIdentityInformations({
                        i_sex: data.Identity.i_sex,
                        s_birthDate: data.Identity.s_birthDate,
                        s_birthGiven: data.Identity.s_birthGiven,
                        s_birthName: data.Identity.s_birthName,
                        s_birthPlace: data.Identity.s_birthPlace,
                        s_given: data.Identity.s_given,
                    }),
                }
            }
            if (data.i_insIdentityResult === 1) {
                // no identity found
                res = {
                    callType: 'IDENTITY_TRAITS',
                    returnType: 'type_01',
                }
            }

            if (res) {
                if (delayed) {
                    insiStore.stackDetails(res)
                    return
                }
                await mutate(res)
            }
        }
        if (data.s_status === 'ERROR') {
            if (data.i_apiErrorCode === 4096) {
                await mutate({
                    callType: 'IDENTITY_TRAITS',
                    returnType: 'type_01',
                })
            } else {
                throw new Error(getInsiErrorMessage(data?.s_apiErrorTlsiErrorSoapReason ?? 'inconnue'))
            }
        }
    }

    return {
        traceInsiCall: {
            verifyPatientInsIdentity,
            getPatientInsIdentityFromVitaleCard,
            getPatientInsIdentityFromIdentityInformation,
        },
        loading,
        error,
    }
}

export default useTraceInsiCall
